import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Roles, useAuth, useTheme, type Role } from './providers';
import { AppFooter } from '../components/AppFooter';
import logoMark from '../assets/logo-mark.png';
import {
  IconBuilding, IconChevronRight, IconLogOut, IconMenu, IconMoon, IconPanelLeft, IconSun,
  NAV_ICONS,
} from '../components/Icons';

type NavItem = { to: string; label: string; roles: Role[] };
type NavGroup = { title: string; items: NavItem[] };

/**
 * The menu, mirroring the role visibility of the old _Sidebar.cshtml.
 *
 * Teacher and Student entries are present but will never render today: the login endpoint
 * refuses those roles at the client's request, so nobody can hold that claim. They are kept
 * so the structure stays uniform and re-enabling is a server-side change only.
 * See KnowledgeBase/findings/disabled-features.md.
 */
const NAV: NavGroup[] = [
  {
    title: 'Overview',
    items: [{ to: '/', label: 'Dashboard', roles: [Roles.Director, Roles.Coordinator, Roles.Cashier] }],
  },
  {
    title: 'Fees',
    items: [
      { to: '/fees/submission', label: 'Fees Submission', roles: [Roles.Director, Roles.Cashier] },
      { to: '/fees', label: 'All Fees', roles: [Roles.Director, Roles.Cashier] },
      { to: '/fees/slips', label: 'Fees Slips', roles: [Roles.Director, Roles.Cashier] },
      { to: '/fees/search', label: 'Advanced Search', roles: [Roles.Director, Roles.Cashier] },
      { to: '/fees/advance', label: 'Generate Advance', roles: [Roles.Director, Roles.Cashier] },
    ],
  },
  {
    title: 'Records',
    items: [
      { to: '/students', label: 'Students', roles: [Roles.Director, Roles.Coordinator] },
      { to: '/teachers', label: 'Teachers', roles: [Roles.Director, Roles.Coordinator] },
      { to: '/staff', label: 'Staff', roles: [Roles.Director, Roles.Coordinator] },
      { to: '/courses', label: 'Courses', roles: [Roles.Director, Roles.Coordinator] },
      { to: '/tests', label: 'Tests', roles: [Roles.Director, Roles.Coordinator] },
    ],
  },
  {
    title: 'Finance',
    items: [
      { to: '/salary/teachers', label: 'Teacher Salary', roles: [Roles.Director] },
      { to: '/salary/staff', label: 'Staff Salary', roles: [Roles.Director] },
      { to: '/expenses', label: 'Expenses', roles: [Roles.Director, Roles.Cashier] },
      { to: '/expenses/summary', label: 'Expense Summary', roles: [Roles.Director] },
    ],
  },
  {
    title: 'Self service',
    items: [
      { to: '/me/info', label: 'My Information', roles: [Roles.Student] },
      { to: '/me/courses', label: 'My Courses', roles: [Roles.Student] },
      { to: '/me/fees', label: 'My Fees', roles: [Roles.Student] },
      { to: '/me/scores', label: 'My Test Scores', roles: [Roles.Student] },
      { to: '/me/teaching', label: 'My Courses', roles: [Roles.Teacher] },
    ],
  },
  {
    title: 'Administration',
    items: [
      { to: '/users', label: 'Users', roles: [Roles.Director, Roles.Coordinator] },
      { to: '/master-information', label: 'Master Information', roles: [Roles.Director] },
    ],
  },
];

const COLLAPSE_KEY = 'ccms.rail.collapsed';

/**
 * Breadcrumb text for the current URL.
 *
 * Derived from the path rather than declared per route, because most screens are two levels
 * deep and a lookup table would need an entry for every id-bearing variant.
 */
function useCrumbs() {
  const { pathname } = useLocation();

  if (pathname === '/') return { section: null, page: 'Dashboard' };

  const parts = pathname.split('/').filter(Boolean);

  const named = NAV.flatMap((g) => g.items).find(
    (item) => item.to !== '/' && pathname.startsWith(item.to),
  );

  const pretty = (value: string) =>
    value
      .replace(/-/g, ' ')
      .replace(/^\w/, (c) => c.toUpperCase());

  const last = parts[parts.length - 1];
  const leaf = /^\d+$/.test(last) ? pretty(parts[parts.length - 2] ?? '') : pretty(last);

  // Only show two levels when the leaf actually says something the section does not.
  // "/fees/submission" matches the "Fees Submission" menu entry, and rendering it as
  // "Fees Submission > Submission" is noise.
  const redundant =
    !!named && named.label.toLowerCase().includes(leaf.toLowerCase());

  if (named && !redundant) {
    return { section: named.label, page: leaf };
  }

  return { section: null, page: named?.label ?? leaf };
}

export function Layout() {
  const { user, logout, hasRole } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const crumbs = useCrumbs();

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1',
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  // Navigating on a phone should close the drawer, or the new page is hidden behind it.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  async function signOut() {
    await logout();
    navigate('/login', { replace: true });
  }

  const groups = NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => hasRole(...item.roles)),
  })).filter((group) => group.items.length > 0);

  const initials = (user?.userName ?? '?').slice(0, 2).toUpperCase();

  return (
    <div className="shell" data-collapsed={collapsed} data-open={drawerOpen}>
      <div
        className="rail__scrim"
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      <aside className="rail">
        <Link to="/" className="rail__brand" aria-label="ZATians' Academy — dashboard">
          {/* The tree from the academy logo. The full lockup is not used here: its wordmark
              is dark navy, which disappears against the graphite rail. The mark carries the
              brand on its own, and the name is set in type so it follows the theme. */}
          <img className="rail__logo" src={logoMark} alt="" width={30} height={30} />
          <div className="rail__wordmark">
            <b>ZATians&rsquo; Academy</b>
            <span>Management</span>
          </div>
        </Link>

        <nav className="rail__nav" aria-label="Main">
          {groups.map((group) => (
            <div className="rail__group" key={group.title}>
              <div className="rail__grouptitle">{group.title}</div>

              {group.items.map((item) => {
                const Icon = NAV_ICONS[item.to];

                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) => `rail__link${isActive ? ' active' : ''}`}
                    title={collapsed ? item.label : undefined}
                  >
                    {Icon && <Icon />}
                    <span className="rail__label">{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="rail__foot">
          <div className="rail__user">
            <div className="rail__avatar" aria-hidden="true">{initials}</div>
            <div className="rail__userinfo">
              <b>{user?.userName}</b>
              <span>{user?.roles.join(', ')}</span>
            </div>
            <button
              type="button"
              className="rail__btn"
              onClick={() => void signOut()}
              title="Sign out"
              aria-label="Sign out"
              style={{ marginLeft: 'auto' }}
            >
              <IconLogOut />
            </button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          {/* Two controls, one per breakpoint: a drawer toggle on narrow screens,
              a collapse toggle on wide ones. */}
          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm"
            onClick={() => setDrawerOpen((open) => !open)}
            aria-label="Open navigation"
            data-mobile-only="true"
          >
            <IconMenu />
          </button>

          <button
            type="button"
            className="btn btn--ghost btn--icon btn--sm"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            data-desktop-only="true"
          >
            <IconPanelLeft />
          </button>

          <nav className="topbar__crumbs" aria-label="Breadcrumb">
            {crumbs.section && (
              <>
                <span>{crumbs.section}</span>
                <IconChevronRight />
              </>
            )}
            <b>{crumbs.page}</b>
          </nav>

          <div className="topbar__right">
            {/* Campus, not role: the role is already on the user card bottom-left, and which
                campus this deployment points at is the thing nothing else on screen tells
                you. Hidden entirely when the institute row has no campus set. */}
            {(user?.campusName || user?.instituteName) && (
              <span className="topbar__campus" title={user?.instituteName ?? undefined}>
                <IconBuilding />
                {user.campusName || user.instituteName}
              </span>
            )}

            <button
              type="button"
              className="btn btn--ghost btn--icon btn--sm"
              onClick={toggle}
              aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
            >
              {theme === 'dark' ? <IconSun /> : <IconMoon />}
            </button>
          </div>
        </header>

        <main className="content">
          <Outlet />
        </main>
      </div>

      {/* Inside the shell so it lines up with the content column and follows the rail's
          collapsed state, rather than spanning the whole window behind the rail. */}
      <AppFooter />
    </div>
  );
}
